import React from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, RefreshCw, TrendingDown } from 'lucide-react';
export function ProblemSection() {
  return <section className="py-20 bg-white relative overflow-hidden" data-id="element-161">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-162">
        {/* Intro */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-16" data-id="element-163">
          <h2 className="text-3xl md:text-4xl font-bold text-amber-950 mb-6" data-id="element-164">
            Y lo peor no es el dinero.
          </h2>
          <p className="text-xl text-amber-900/80 leading-relaxed" data-id="element-165">
            Lo peor es esa sensación constante de no poder relajarte. De no
            poder darte un gusto sin culpa. De sentir que, por más que te
            esfuerzas, siempre llegas justa.
          </p>
        </motion.div>

        {/* The Real Problem */}
        <div className="grid gap-12 mb-20" data-id="element-166">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="bg-amber-50 p-8 rounded-2xl border border-amber-100 shadow-sm" data-id="element-167">
            <div className="flex gap-4 items-start" data-id="element-168">
              <AlertCircle className="w-8 h-8 text-amber-600 flex-shrink-0 mt-1" data-id="element-169" />
              <div data-id="element-170">
                <h3 className="text-xl font-bold text-amber-900 mb-3" data-id="element-171">
                  Tal vez incluso te dices cosas como:
                </h3>
                <p className="text-amber-800 italic mb-6" data-id="element-172">
                  “¿En qué se me fue?” “¿Por qué nunca me alcanza?” “¿Qué estoy
                  haciendo mal?”
                </p>
                <div className="bg-white p-6 rounded-xl border-l-4 border-amber-500 shadow-sm" data-id="element-173">
                  <p className="font-bold text-lg text-amber-950 mb-2" data-id="element-174">
                    Déjame decirte algo importante desde el principio:
                  </p>
                  <ul className="space-y-2" data-id="element-175">
                    <li className="flex items-center gap-2 text-amber-900" data-id="element-176">
                      <span data-id="element-177">👉</span> El problema no es que ganes poco.
                    </li>
                    <li className="flex items-center gap-2 text-amber-900" data-id="element-178">
                      <span data-id="element-179">👉</span> El problema es que nadie te enseñó a
                      manejar la quincena en la vida real.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>

          {/* The Lie */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="prose prose-lg text-amber-900/80 mx-auto" data-id="element-180">
            <p data-id="element-181">
              Nos hicieron creer que si el dinero no alcanza es porque no somos
              disciplinadas. Porque gastamos mal. Porque “vivimos por encima de
              nuestras posibilidades”.
            </p>
            <p className="font-bold text-amber-950" data-id="element-182">
              Pero esa es una mentira cómoda.
            </p>
            <p data-id="element-183">
              La verdad es esta: El sistema financiero que nos enseñaron no está
              diseñado para personas reales. Está hecho para sueldos altos.
              Gastos estables. Cero urgencias. Cero estrés.
            </p>
            <p data-id="element-184">
              No para mujeres que viven quincena a quincena. Con gastos
              impredecibles. Con responsabilidades. Con cansancio mental
              acumulado.
            </p>
          </motion.div>
        </div>

        {/* The Cycle */}
        <div className="bg-amber-900 text-amber-50 rounded-3xl p-8 md:p-12 shadow-xl relative overflow-hidden" data-id="element-185">
          <div className="absolute top-0 right-0 w-64 h-64 bg-amber-800/30 rounded-full blur-3xl -mr-32 -mt-32" data-id="element-186" />

          <div className="relative z-10" data-id="element-187">
            <div className="flex items-center gap-3 mb-6" data-id="element-188">
              <RefreshCw className="w-6 h-6 text-amber-400 animate-spin-slow" data-id="element-189" />
              <h3 className="text-2xl font-bold text-amber-100" data-id="element-190">
                El ciclo que se repite cada quincena
              </h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-id="element-191">
              {['Cobras y te alivias', 'Gastas y te justificas', 'Te aprietas y te frustras', 'Llegas justa y te culpas'].map((step, i) => <div key={i} className="bg-amber-800/50 p-4 rounded-lg border border-amber-700/50 text-center text-sm font-medium" data-id="element-192">
                  {step}
                </div>)}
            </div>

            <p className="text-lg text-amber-200/90 mb-8" data-id="element-193">
              Y poco a poco, sin darte cuenta, se instala algo peor que la falta
              de dinero:{' '}
              <span className="text-white font-bold" data-id="element-194">
                la ansiedad constante.
              </span>
            </p>

            <div className="bg-amber-950/50 p-6 rounded-xl border border-amber-800/50" data-id="element-195">
              <h4 className="font-bold text-amber-100 mb-2 flex items-center gap-2" data-id="element-196">
                <TrendingDown className="w-5 h-5 text-red-400" data-id="element-197" />
                ¿Por qué fallan los métodos tradicionales?
              </h4>
              <p className="text-amber-200/80 text-sm" data-id="element-198">
                La mayoría de las soluciones intentan que gastes menos, que te
                restrinjas, que te sacrifiques. Pero el problema real no es
                cuánto gastas. El problema es cómo fluye tu dinero entre
                quincenas. Tu sueldo no desaparece. Se fuga. Y nadie te enseñó a
                ver esas fugas invisibles.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}